import { FC, useContext, useEffect, useMemo, useState, useCallback } from "react";
import SelectDropdown from "../../Shared/SelectDropdown/SelectDropdown";
import TaskPriority from "../../Shared/TaskPriority";
import TaskType from "../../Shared/TaskType";
import SelectDropdownWrapper from "../../Shared/SelectDropdownWrapper/SelectDropdownWrapper";
import { Context } from "../../..";
import { AxiosError } from "axios";
import { AppContext } from "../../../App";
import PersonLineDisplay from "../../Shared/PersonLineDisplay";
import { fullNameFormat } from "../../../helpers/Inicials";
import { IDashBoard } from "../../../common/api_interfaces/board/IDashBoard";
import "./TaskActions.css";
import BtnDelete from "../../buttons/btnDelete.component/BtnDelete";
import { ColumnBoardList } from "../../Shared/ColumnBoardList/ColumnBoardList";
import { TaskContext } from "../../../contexts/TaskContext";
import { IColumn } from "../../../common/api_interfaces/column/IColumn";
import { ITeamMemberDisplay } from "../../../common/api_interfaces/project/ITeamDisplay";
import { IGetTeamDisplay } from "../../../common/api_interfaces/project/IGetTeamDisplay";

export interface TaskActionOption {
    name: string;
    entityName: string;
    code: string;
}

export interface IdName {
    id: number;
    name: string;
}

export interface Priority {
    id: number;
    name: string;
    color: string;
}

export interface Type {
    id: number;
    name: string;
    color: string;
}

enum ActionEntity {
    changePriority = "changePriority",
    changeType = "changeType",
    changeResponsible = "changeResponsible",
    move = "move"
}

const ACTION_OPTIONS = [
    {
        name: "Изменить приоритет",
        entityName: "Приоритет",
        code: ActionEntity.changePriority,
    },
    {
        name: "Изменить тип",
        entityName: "Тип",
        code: ActionEntity.changeType,
    },
    {
        name: "Изменить исполнителя",
        entityName: "Исполнитель",
        code: ActionEntity.changeResponsible,
    },
    {
        name: "Переместить",
        entityName: "Колонка/Доска",
        code: ActionEntity.move,
    },
] as const;

interface TaskActionsProps {
    onChangePriority: React.Dispatch<React.SetStateAction<Priority | undefined>>;
    onChangeType: React.Dispatch<React.SetStateAction<Type | undefined>>;
    onChangeResponsible: React.Dispatch<React.SetStateAction<ITeamMemberDisplay | null | undefined>>;
    onChangeColumn: (column: IdName | undefined) => void;
    onChangeBoard: (board: IdName | undefined) => void;
    selectedActions: TaskActionOption[];
    setSelectedActions: React.Dispatch<React.SetStateAction<TaskActionOption[]>>;
}

const TaskActions: FC<TaskActionsProps> = ({
    onChangeColumn,
    onChangeBoard,
    onChangeResponsible,
    onChangeType,
    onChangePriority,
    setSelectedActions,
    selectedActions,
}) => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    const { taskInformation: task, allPerson, currentPageBoardId, projectSettings, forceLinkExecutor } = useContext(TaskContext);

    const [team, setTeam] = useState<ITeamMemberDisplay[]>([]);
    const [columns, setColumns] = useState<IColumn[]>([]);
    const [boards, setBoards] = useState<IDashBoard[]>([]);
    const [currentColumn, setCurrentColumn] = useState<IdName>();
    const [currentBoard, setCurrentBoard] = useState<IdName>();
    const [currentResponsible, setCurrentResponsible] = useState<ITeamMemberDisplay | null>(null);

    const responsibleRequired = useMemo(() => projectSettings.responsibleRequired, [projectSettings]);

    // Мемоизированные значения по умолчанию
    const defaultColumn = useMemo(() => {
        if (!task || !columns.length) return undefined;
        if (task.mainBoard?.id === currentPageBoardId) return columns.find((c) => c.id === task.columns.main?.id);
        if (task.proxyBoard?.id === currentPageBoardId) return columns.find((c) => c.id === task.columns.proxy?.id);
        return undefined;
    }, [columns, task, currentPageBoardId]);

    const defaultType = useMemo(() => task?.type.find((t) => t.current), [task]);
    const defaultPriority = useMemo(() => task?.priority.find((p) => p.current), [task]);

    // Мемоизированные списки для выбора
    const typesToSelect = useMemo(() => (task ? task.type.filter((t) => !t.current) : []), [task]);
    const prioritiesToSelect = useMemo(() => (task ? task.priority.filter((p) => !p.current) : []), [task]);

    const responsiblesToSelect = useMemo(
        () => (allPerson?.executor?.id ? team.filter((p) => p.id !== allPerson.executor?.id) : team),
        [team, allPerson]
    );

    const columnsToSelect = useMemo(
        () => (defaultColumn ? columns.filter((c) => c.id !== defaultColumn.id) : columns),
        [defaultColumn, columns]
    );

    const boardsToSelect = useMemo(
        () => (task ? boards.filter((b) => b.id !== task.mainBoard?.id && b.id !== task.proxyBoard?.id) : []),
        [task, boards]
    );

    const optionsToSelect = useMemo(
        () => ACTION_OPTIONS.filter((opt) => !forceLinkExecutor || opt.code !== ActionEntity.changeResponsible ).filter((opt) => !selectedActions.some((selected) => selected.code === opt.code)),
        [selectedActions, forceLinkExecutor]
    );

    // Загрузка данных с использованием useCallback для стабильных ссылок
    const loadData = useCallback(async () => {
        if (!task || !currentPageBoardId) return;

        try {
            const filter: IGetTeamDisplay = {
                filters: {
                    projectId: task.projectId,
                    role: undefined,
                    name: undefined,
                }
            };

            const [teamRes, columnsRes, boardsRes] = await Promise.all([
                store.projectService.getProjectTeamDisplayData(filter),
                store.dashBoardService.getColumns(currentPageBoardId),
                store.dashBoardService.getProjectBoards(task.projectId),
            ]);

            setTeam(teamRes.data.team);
            setColumns(columnsRes.data);
            setBoards(boardsRes.data);
        } catch (err) {
            if (err instanceof AxiosError) {
                showToast(err.response?.data.message);
            }
        }
    }, [task, currentPageBoardId, store, showToast]);

    useEffect(() => {
        if (team && allPerson) {
            let resp = team.find((member) => member.id === allPerson?.executor?.id) ?? null;
            setCurrentResponsible(resp);
        }
    }, [team, allPerson]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Обработчики событий
    const addAction = useCallback(
        (action: TaskActionOption) => {
            setSelectedActions((prev) => [...prev, action]);
        },
        [setSelectedActions]
    );

    const removeAction = useCallback(
        (action: TaskActionOption) => {
            switch (action.code) {
                case ActionEntity.changePriority:
                    onChangePriority(undefined);
                    break;
                case ActionEntity.changeResponsible:
                    onChangeResponsible(undefined);
                    setCurrentResponsible(null);
                    break;
                case ActionEntity.changeType:
                    onChangeType(undefined);
                    break;
                case ActionEntity.move:
                    if (currentBoard !== undefined) {
                        setCurrentBoard(undefined);
                        onChangeBoard(undefined);
                    } else if (currentColumn !== undefined) {
                        setCurrentColumn(undefined);
                        onChangeColumn(undefined);
                    }
                    break;
            }
            if (action.entityName === ActionEntity.changePriority) { }
            setSelectedActions((prev) => prev.filter((act) => act.code !== action.code));
        },
        [setSelectedActions]
    );

    const handleColumnClick = useCallback(
        (column: IdName) => {
            onChangeColumn(column);
            setCurrentColumn(column);
            setCurrentBoard(undefined);
        },
        [onChangeColumn]
    );

    const handleBoardClick = useCallback(
        (board: IdName) => {
            onChangeBoard(board);
            setCurrentBoard(board);
            setCurrentColumn(undefined);
        },
        [onChangeBoard]
    );

    const changeResponsible = useCallback(
        (responsible: ITeamMemberDisplay | null) => {
            setCurrentResponsible(responsible);
            onChangeResponsible(responsible);
        },
        [onChangeResponsible]
    );

    // Рендер функции для различных типов действий
    const renderActionContent = useCallback(
        (action: TaskActionOption) => {
            switch (action.code) {
                case "changePriority":
                    return (
                        <SelectDropdownWrapper
                            items={prioritiesToSelect}
                            defaultItem={defaultPriority}
                            onChange={onChangePriority}
                            renderItem={(priority) => {
                                const priorityStyle = JSON.parse(priority.color);
                                return (
                                    <TaskPriority
                                        name={priority.name}
                                        color={priorityStyle.color}
                                        icon={priorityStyle.icon}
                                    />
                                );
                            }}
                        />
                    );

                case "changeType":
                    return (
                        <SelectDropdownWrapper
                            items={typesToSelect}
                            defaultItem={defaultType}
                            onChange={onChangeType}
                            renderItem={(type) => {
                                const typeStyle = JSON.parse(type.color);
                                return <TaskType name={type.name} color={typeStyle.color} icon={typeStyle.icon} />;
                            }}
                        />
                    );

                case "changeResponsible":
                    return (
                        <div className="select-dropdown-wrapper-container">
                            <SelectDropdown
                                showResetButton={!responsibleRequired && currentResponsible !== null && allPerson?.executor !== undefined}
                                onReset={() => changeResponsible(null)}
                            >
                                <SelectDropdown.Selected>
                                    {currentResponsible ? (
                                        <PersonLineDisplay
                                            userId={currentResponsible.id}
                                            name={fullNameFormat(currentResponsible, "s N M")}
                                        />
                                    ) : (
                                        <PersonLineDisplay name={"Не выбран"} />
                                    )}
                                </SelectDropdown.Selected>
                                {responsiblesToSelect.map((responsible) => (
                                    <SelectDropdown.Option>
                                        <div onClick={() => changeResponsible(responsible)}>
                                            <PersonLineDisplay
                                                userId={responsible.id}
                                                name={fullNameFormat(responsible, "s N M")}
                                            />
                                        </div>
                                    </SelectDropdown.Option>
                                ))}
                            </SelectDropdown>
                        </div>
                    );

                case "move":
                    return (
                        <div className="select-dropdown-wrapper-container">
                            <ColumnBoardList
                                columns={columnsToSelect}
                                boards={boardsToSelect}
                                selectedColumn={currentColumn}
                                onClickColumn={handleColumnClick}
                                selectedBoard={currentBoard}
                                onClickBoard={handleBoardClick}
                                defaultColumn={defaultColumn}
                            />
                        </div>
                    );

                default:
                    return null;
            }
        },
        [
            prioritiesToSelect,
            defaultPriority,
            onChangePriority,
            typesToSelect,
            defaultType,
            onChangeType,
            responsiblesToSelect,
            onChangeResponsible,
            columnsToSelect,
            boardsToSelect,
            currentColumn,
            currentBoard,
            defaultColumn,
            handleColumnClick,
            handleBoardClick,
            currentResponsible,
            responsibleRequired
        ]
    );

    if (!task) return null;

    return (
        <div className="task-actions-container">
            {optionsToSelect.length > 0 && (
                <>
                    <SelectDropdown className="task-actions-selector">
                        <SelectDropdown.Selected>Добавить действие</SelectDropdown.Selected>
                        {optionsToSelect.map((opt) => (
                            <SelectDropdown.Option key={opt.code}>
                                <div className="action-option" onClick={() => addAction(opt)}>
                                    {opt.name}
                                </div>
                            </SelectDropdown.Option>
                        ))}
                    </SelectDropdown>
                    <div className="divider" />
                </>
            )}

            {selectedActions.map((action) => (
                <div key={action.code} className="action-container">
                    <div className="action-container-left">
                        {renderActionContent(action)}
                        <div className="action-entity-name">{action.entityName}</div>
                    </div>
                    <BtnDelete onClickFunc={() => removeAction(action)} permission={true} />
                </div>
            ))}
        </div>
    );
};

export default TaskActions;
